import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, Subject, catchError, map, throwError } from 'rxjs';
import { ApiConstants } from 'src/app/core/constants/api.constants';
import { TuwaiqConfig } from 'src/app/core/constants/tuwaiq.config';
import { FilterDetails } from 'src/app/core/models/filter-details.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { LANGUAGES } from 'src/app/core/translate/translate.service';
import { TuwaiqUtil } from 'src/app/core/TuwaiqUtil';
import { SessionExpiredComponent } from 'src/app/shared/components/sessionExpired/sessionExpired/session-expired.component';
import { Department } from 'src/app/ticketing-module/util/model/departmen.model';
import { reportTableConstants } from '../constants/reportTable.constants';
import { CancelAuditReportData, CloseAuditReportData, CorrectivePlan, Findings, Observation } from '../models/audit-report.model';
import { AuditType } from '../models/auditType.model';
import { IDevice } from '../models/device.model';
import { ReportDevicesChangedTableColumns, ReportDevicesTableColumns, ReportTableColumns } from '../models/report-table-columns.model';
import { ReportStatus } from '../models/reportStatus.model';
import { Stakeholder } from '../models/stakeholder.model';

@Injectable({
    providedIn: 'root'
})
export class PeriodicModuleService {
    filterServiceList = new BehaviorSubject<FilterDetails[]>([]);
    resetServiceList = new BehaviorSubject<boolean>(false);
    reloadSideMenuCount = new BehaviorSubject<boolean>(false);
    currentStatusId = new BehaviorSubject<string>('');
    isExpiredSession: any;
    fetchNextPage = new Subject<number>();
    showTableLoader = new Subject<boolean>();
    scrollToTop = new Subject<boolean>();
    statusId!: string;
    lang: LANGUAGES;
    downloadPdfRequest = new Subject<number>();
    devicesReportUpdateList = new BehaviorSubject<IDevice[]>([]);

    constructor(private http: HttpClient, private authService: AuthService, public dialog: MatDialog, private activatedRoute: ActivatedRoute) { }

    public getAllSectionReports(params?): Observable<any> {
        return this.http.get(ApiConstants.All_SECTIONS_REPORTS, { params: params, observe: 'response' }).pipe(
            map(response => {
                const responseData = response.body;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    exportDevices(params?: any) {
        let api = ApiConstants.EXPORT_DEVICES;
        const queryParams = this.exportQueryParams(params);
        return this.http.get(api, { params: queryParams, responseType: 'blob', observe: 'response' });
    }

    exportQueryParams(params?: any): any {
        let queryParams: any = {};
        if (params) {
            if (params.fromPeriodStart) {
                queryParams['from-period-start'] = params.fromPeriodStart;
            }
            if (params.fromPeriodEnd) {
                queryParams['from-period-end'] = params.fromPeriodEnd;
            }
        }
        return queryParams;
    }

    getReportList(parameters = {}) {
        let api = ApiConstants.REPORT_LIST;
        const body = this.buildQueryParams(parameters);
        return this.http.get(api, { params: body, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getDeviceChangedList(parameters, deviceId) {
        let api = ApiConstants.DEVICE_CHANGED_LIST.replace('{deviceId}', deviceId.toString());
        const body = this.buildQueryParams(parameters);
        return this.http.get(api, { params: body, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getReportDeviceList(parameters?) {
        let api = ApiConstants.REPORT_LIST;
        const body = this.buildQueryParams(parameters);
        return this.http.get(api, { params: body, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getReportDeviceAllItemsList(parameters?) {
        let api = ApiConstants.REPORT_LIST_DEVICE_All_ITEMS;
        const body = this.buildQueryParams(parameters);
        return this.http.get(api, { params: body, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getReportById(reportId: string | number) {
        let api = ApiConstants.GET_REPORT_BY_ID.replace('{reportId}', reportId.toString());
        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getReportDeviceById(reportId: string | number) {
        let api = ApiConstants.GET_REPORT_DEVICE_BY_ID.replace('{reportId}', reportId.toString());
        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    getMyReportDevicesById(reportId: string | number) {
        let api = ApiConstants.GET_MY_REPORT_DEVICES_BY_ID.replace('{reportId}', reportId.toString());
        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    exportExcelReport(reportId: string | number, dateTime: string) {
        let api = ApiConstants.EXPORT_EXCEL_REPORT_BY_ID.replace('{reportId}', reportId.toString());
        api += `?current-date-time=${encodeURIComponent(dateTime)}`;
        return this.http.get(api, { responseType: 'blob', observe: 'response' });
    }
    exportExcelDeviceReport(reportId: string | number, dateTime: string) {
        let api = ApiConstants.EXPORT_EXCEL_DEVICE_REPORT_BY_ID.replace('{reportId}', reportId.toString());
        api += `?current-date-time=${encodeURIComponent(dateTime)}`;
        return this.http.get(api, { responseType: 'blob', observe: 'response' });
    }
    exportWordReport(parameters) {
        let api = ApiConstants.EXPORT_WORD_REPORT
        const body = this.buildQueryParams(parameters);
        return this.http.get(api, { params: body, responseType: 'blob', observe: 'response' });
    }

    addObservation(reportId: string | number, observation: Observation) {
        let api = ApiConstants.ADD_REPORT_OBSERVATION.replace('{reportId}', reportId.toString());
        return this.http.put(api, observation).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    addFindings(reportId: string | number, findings: Findings) {
        let api = ApiConstants.ADD_REPORT_FINDINGS.replace('{reportId}', reportId.toString());
        return this.http.put(api, findings).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    addCorrectivePlan(reportId: string | number, correctivePlan: CorrectivePlan) {
        let api = ApiConstants.ADD_REPORT_CORRECTIVEPLAN.replace('{reportId}', reportId.toString());
        return this.http.put(api, correctivePlan).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    closeAuditReport(reportId: string | number, reportData: CloseAuditReportData) {
        let api = ApiConstants.CLOSE_AUDIT_REPORT.replace('{reportId}', reportId.toString());
        return this.http.put(api, reportData).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    cancelReport(reportId: string | number, reportData: CancelAuditReportData) {
        let api = ApiConstants.CANCEL_AUDIT_REPORT.replace('{reportId}', reportId.toString());
        return this.http.put(api, reportData).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    //this.lang === LANGUAGES.AR ? response.performer.nameAr : response.performer.nameEn, response.reportTypeName
    convertResponseToTableData(response: any) {
        return new ReportTableColumns(
            response.code,
            response.creator?.name,
            response.auditType,
            response?.reportStatus?.name,
            response.createdAt,
            response.closedAt,
            response.id,
            response.reportStatus?.code,
            response.creator?.id
        );
    }
    convertResponseToTableDataLogistic(response: any) {
        //response.reportStatus.name,response.reportStatus.id
        return new ReportTableColumns(
            response.code,
            response.creator?.name,
            response.auditType,
            response?.reportStatus?.name,
            response.createdAt,
            response.closedAt,
            response.id,
            response.reportStatus?.code,
            response.creator?.id
        );
    }
    convertResponseToTableDataLogisticDevicesAllItems(response: any) {
        //response.reportStatus.name,response.reportStatus.id
        return new ReportDevicesTableColumns(
            response.serialNumber,
            response.capacity,
            response.deviceName,
            response.owner?.id,
            this.lang === LANGUAGES.AR ? response.owner?.nameAr : response.owner?.nameEn,
            response.department?.id,
            response.department?.name,
            response.department?.departmentCode,
            response.requestDate,
            response.returnedDate,
            response.deliveryDate,
            response?.status?.name,
            response.status?.code,
            response?.id,
            response?.owner?.image
        );
    }
    convertResponseToTableDataDevicesChanged(response: any) {
        return new ReportDevicesChangedTableColumns(
            response.field,
            response.oldValue,
            response.newValue,
            response.user?.id,
            this.lang === LANGUAGES.AR ? response.user?.name : response.user?.name,
            response.user?.email,
            response.user?.userImage,
            response.changeSource,
            response.createdAt
        );
    }
    getTableColumns(requestObjectName: string) {
        // return reportTableConstants[requestObjectName].tableColumn;

        const tableConfig = reportTableConstants[requestObjectName];
        if (!tableConfig || !tableConfig.tableColumn) {
            console.warn(`No table configuration found for: ${requestObjectName}`);
            return []; // Return an empty array or handle as needed
        }
        return tableConfig.tableColumn;

    }

    public getAllStakeholders(): Observable<Stakeholder[]> {
        return this.http
            .get(ApiConstants.STAKEHOLDERS_USERS_LIST)
            .pipe(map((response: { data: Stakeholder[]; meta: any }) => TuwaiqUtil.convertObjectKeysToCamelCase(response.data)));
    }

    public getAllAuditType(): Observable<AuditType[]> {
        return this.http
            .get(ApiConstants.AUDIT_TYPE_LIST)
            .pipe(map((response: { data: AuditType[]; meta: any }) => TuwaiqUtil.convertObjectKeysToCamelCase(response.data)));
    }

    public getReportUsers(parameters?) {
        let api = ApiConstants.REPORT_USERS_LIST;
        const body = this.buildQueryParams(parameters);
        return this.http.get(api, { params: body, observe: 'response' }).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public getReportOwners(parameters?) {
        let api = ApiConstants.REPORT_OWNERS_LIST;
        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public getCommentsUsers(reportId) {
        let api = ApiConstants.COMMENTS_REPORT_AUDITOR.replace('{reportId}', reportId);
        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    public getReportSTATUS(): Observable<ReportStatus[]> {
        return this.http
            .get(ApiConstants.REPORT_STATUS_LIST)
            .pipe(map((response: { data: ReportStatus[]; meta: any }) => TuwaiqUtil.convertObjectKeysToCamelCase(response.data)));
    }

    public getReportDevicesSTATUS(): Observable<ReportStatus[]> {
        return this.http
            .get(ApiConstants.REPORT_DEVICES_STATUS_LIST)
            .pipe(map((response: { data: ReportStatus[]; meta: any }) =>
                TuwaiqUtil.convertObjectKeysToCamelCase(response.data)));
    }
    public getReportDevicesAllItemsSTATUS(): Observable<ReportStatus[]> {
        return this.http.get(ApiConstants.REPORT_DEVICES_STATUS_ALL_ITEMS_LIST).pipe(
            map(response => {
                return TuwaiqUtil.convertObjectKeysToCamelCase(response);
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    public getAllUsersDeviceChanged(deviceId) {
        let api = ApiConstants.EDITORS_DEVICE_LIST.replace('{deviceId}', deviceId);
        return this.http.get(api).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    public getAllFieldName(): Observable<any[]> {
        //let api = ApiConstants.FIELD_NAME_LIST;
        return this.http.get(ApiConstants.FIELD_NAME_LIST)
            .pipe(map((response: { data: any[]; meta: any }) =>
                TuwaiqUtil.convertObjectKeysToCamelCase(response.data)));
    }
    public createAuditReport(report) {
        const api = ApiConstants.SAVE_REPORT;
        return this.http.post(api, report).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public createDeviceReport(device) {
        const api = ApiConstants.ADD_DEVICE;
        return this.http.post(api, device).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public editDeviceReport(device, deviceId) {
        const api = ApiConstants.EDIT_DEVICE.replace('{deviceId}', deviceId);
        return this.http.patch(api, device).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public editMyReportDevice(devices, reportId) {
        const api = ApiConstants.UPDATE_MY_REPORT_DEVICE.replace('{reportId}', reportId);
        return this.http.put(api, devices).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    public createReportForDevice(report) {
        const api = ApiConstants.ADD_DEVICE_REPORT;
        return this.http.post(api, report).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public addCommentReportAudit(reportComment) {
        const api = ApiConstants.REPORT_COMMENT_CREATOR;
        return this.http.post(api, reportComment).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    public addCommentReportStakeholderAudit(reportComment) {
        const api = ApiConstants.REPORT_COMMENT_Stakeholders;
        return this.http.post(api, reportComment).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getAllDepartments(): Observable<Department[]> {
        return this.http.get(ApiConstants.All_DEPARTMENTS).pipe(map((response: { data: Department[]; meta: any }) => response.data));
    }

    getAllStatus() {
        return this.http.get(ApiConstants.DEVICES_STATUS).pipe(
            map(response => {
                return response;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    findUsersByDepartment(departmentId) {
        const api = ApiConstants.GET_USER_BY_DEPARTMENT.replace('{departmentId}', departmentId.toString());

        return this.http.get(api).pipe(
            map((response: any) => {
                return TuwaiqUtil.convertObjectKeysToCamelCase(response.data);
            }),
            catchError(this.errorHandler.bind(this))
        );
    }

    getDeviceById(deviceId) {
        const api = ApiConstants.GET_DEVICE_BY_ID.replace('{deviceId}', deviceId.toString());

        return this.http.get(api).pipe(
            map((response: any) => {
                return TuwaiqUtil.convertObjectKeysToCamelCase(response.data);
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    handleAttachmentObject(attachmentList, action) {
        return attachmentList.map(attachment => ({
            checksum: TuwaiqConfig.ATTACHMENT_CHECKSUM,
            'content-type': attachment.type,
            key: attachment.key,
            name: attachment.name,
            size: attachment.size,
            action: action
        }));
    }

    getTableHeaders(requestObjectName: string) {
        // const displayColumns = reportTableConstants[requestObjectName].tableColumn.map(c => c.columnDef);
        // return displayColumns;

        const displayColumns = reportTableConstants[requestObjectName];
        if (!displayColumns || !displayColumns.tableColumn) {
            console.warn(`No table configuration found for: ${requestObjectName}`);
            return []; // Return an empty array or handle as needed
        }
        return displayColumns.tableColumn.map(c => c.columnDef);
    }

    buildQueryParams(source: Object): HttpParams {
        let target: HttpParams = new HttpParams();
        Object.keys(source).forEach((key: string) => {
            const value: string | number | boolean | Date | Object = source[key];
            if (typeof value !== 'undefined' && value !== null) {
                target = target.append(key, value.toString());
            }
        });
        return target;
    }

    /** Error Handling method */
    errorHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            // this.authService.logout()
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors'][0]);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    //TEMP:
    errorListHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            // this.authService.logout()
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors']);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    handleSessionExpired() {
        if (!this.isExpiredSession) {
            this.dialog.open(SessionExpiredComponent, {
                width: '412px',
                disableClose: true
            });
            this.isExpiredSession = true;
        }
    }
    public closeReportDevice(payLoad, reportId) {
        const api = ApiConstants.CLOSE_REPORT_DEVICE.replace('{reportId}', reportId);
        return this.http.put(api, payLoad).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
    public cancelReportDevice(reportId,payLoad) {
        const api = ApiConstants.CANCEL_REPORT_DEVICE.replace('{reportId}', reportId);
        return this.http.put(api, payLoad).pipe(
            map(response => {
                const responseData = response;
                TuwaiqUtil.convertObjectKeysToCamelCase(response);
                return responseData;
            }),
            catchError(this.errorHandler.bind(this))
        );
    }
}
